/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a drugShipmentDepotBatch.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugShipmentDepotBatchState {
    drugShipmentDepotBatch: Dtos.DrugShipmentDepotBatch | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugShipmentDepotBatchState {
    drugShipmentDepotBatch: IInitialDrugShipmentDepotBatchState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentDepotBatchInitialState: IInitialDrugShipmentDepotBatchState = {
    drugShipmentDepotBatch: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentDepotBatchSelector: TypedUseSelectorHook<IDrugShipmentDepotBatchState> = useSelector;

export const DrugShipmentDepotBatchSelectors = {
    selectDrugShipmentDepotBatch: (state: IDrugShipmentDepotBatchState) => state.drugShipmentDepotBatch.drugShipmentDepotBatch,
    selectLoadState: (state: IDrugShipmentDepotBatchState) => state.drugShipmentDepotBatch.loadState,
    selectSaveState: (state: IDrugShipmentDepotBatchState) => state.drugShipmentDepotBatch.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugShipmentDepotBatchReducer extends ImmerReducer<IInitialDrugShipmentDepotBatchState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugShipmentDepotBatch?: Dtos.DrugShipmentDepotBatch) {
        this.draftState.drugShipmentDepotBatch = drugShipmentDepotBatch ? drugShipmentDepotBatch : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipmentDepotBatch = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.DrugShipmentDepotBatch) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(drugShipmentDepotBatch?: Dtos.DrugShipmentDepotBatch) {
        this.draftState.drugShipmentDepotBatch = drugShipmentDepotBatch ? drugShipmentDepotBatch : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipmentDepotBatch = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugShipmentDepotBatchInitialState };
    }
};

export const DrugShipmentDepotBatchActions = createActionCreators(DrugShipmentDepotBatchReducer);
const reducer = createReducerFunction(DrugShipmentDepotBatchReducer, DrugShipmentDepotBatchInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugShipmentDepotBatchApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetDrugShipmentDepotBatchById({
            id
        }));
    },
    save: (drugShipmentDepotBatch?: Dtos.DrugShipmentDepotBatch) => {
        return client.post(new Dtos.SetDrugShipmentDepotBatch({
            drugShipmentDepotBatch
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentDepotBatchLogic = {
    loadById: createLogic<IDrugShipmentDepotBatchState, {}, undefined, string, ReturnType<typeof DrugShipmentDepotBatchActions.loadById>>({
        type: DrugShipmentDepotBatchActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await DrugShipmentDepotBatchApi.loadById(id);

                dispatch(DrugShipmentDepotBatchActions.loadSuccess(
                    response.drugShipmentDepotBatch
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentDepotBatchActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IDrugShipmentDepotBatchState, {}, undefined, string, ReturnType<typeof DrugShipmentDepotBatchActions.save>>({
        type: DrugShipmentDepotBatchActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const drugShipmentDepotBatch = action.payload;

            try {
                const response = await DrugShipmentDepotBatchApi.save(drugShipmentDepotBatch);

                dispatch(DrugShipmentDepotBatchActions.saveSuccess(
                    response.drugShipmentDepotBatch
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentDepotBatchActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugShipmentDepotBatchLogic.loadById,
    DrugShipmentDepotBatchLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentDepotBatch = (): [Dtos.DrugShipmentDepotBatch | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof DrugShipmentDepotBatchActions] => {
    const drugShipmentDepotBatch = useSelector(DrugShipmentDepotBatchSelectors.selectDrugShipmentDepotBatch);
    const loadState = useSelector(DrugShipmentDepotBatchSelectors.selectLoadState);
    const saveState = useSelector(DrugShipmentDepotBatchSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugShipmentDepotBatchActions>(() => {
        return bindActionCreators(DrugShipmentDepotBatchActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugShipmentDepotBatchActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugShipmentDepotBatch,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugShipmentDepotBatch', reducer as any, logics as any);